import TKCustomElementFactory from '@tk/utilities/tk.custom.element.factory';

export default class DIWRadio extends TKCustomElementFactory {
    radios: NodeListOf<HTMLInputElement>;

    constructor() {
        super();

        this.radios = this.querySelectorAll('input[type="radio"]');
    }

    connectedCallback(): void {
        // Add event listeners to all radio buttons
        this.radios.forEach((radio) => {
            radio.addEventListener('change', (event) => this.handleChange(event));
        });
    }

    /**
     * Returns the value of the selected radio button
     */
    get value(): string {
        const checkedRadio = Array.from(this.radios).find((radio) => radio.checked);
        return checkedRadio ? checkedRadio.value : '';
    }

    /**
     * Sets a radio button as selected based on the provided value
     */
    set value(val: string) {
        this.radios.forEach((radio) => {
            radio.checked = radio.value === val;
        });
    }

    /**
     * Returns the name of the radio button group
     */
    get name(): string {
        return this.radios.length > 0 ? this.radios[0].name : '';
    }

    /**
     * Checks if any radio button is selected
     */
    get checked(): boolean {
        return Array.from(this.radios).some((radio) => radio.checked);
    }

    /**
     * Returns the selected radio button element
     */
    get selectedRadio(): HTMLInputElement | null {
        return Array.from(this.radios).find((radio) => radio.checked) || null;
    }

    /**
     * Handler for radio button changes
     * Only dispatches event when a radio button becomes checked (not unchecked)
     */
    private handleChange(event: Event): void {
        const target = event.target as HTMLInputElement;

        // Only dispatch event when radio button is checked (selected), not when unchecked
        if (!target.checked) return;

        // Dispatch custom event so external components can react to changes
        this.dispatchEvent(
            new CustomEvent('change', {
                detail: {
                    value: this.value,
                    name: this.name,
                    selectedRadio: this.selectedRadio,
                },
                bubbles: true,
                composed: true,
            }),
        );
    }

    /**
     * Resets all radio buttons
     */
    reset(): void {
        this.radios.forEach((radio) => {
            radio.checked = false;
        });
    }
}
